// frontend/src/ChatWindow.js
import React, { useState, useEffect, useRef, useCallback, useMemo, useLayoutEffect, forwardRef, useImperativeHandle } from 'react';
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';
import Markdown from 'markdown-to-jsx';
import CheatsheetModal from './components/CheatsheetModal';
import './ChatWindow.css';
import './chat/chat.css';
import ChatHistorySidebar from './ChatHistorySidebar';
import ChatSettingsModal from './ChatSettingsModal';
import ExplorerLogModal from './ExplorerLogModal';
import DocumentViewerModal from './DocumentViewerModal';
import ProtectedElement from './ProtectedElement';
import { usePermissions } from './PermissionsContext';
import { useChatDisplay } from './chat/useChatDisplay';
import { ChatMessagesList } from './chat/ChatMessagesList.jsx';
import { annotateAgentSteps } from './chat/useAgentSteps';
import { buildMarkdownOptions, makeUUIDProcessor } from './chat/markdownOptions';
import { streamSSE } from './chat/streamSSE';


// --- Configuration ---
const BASE_URL = process.env.REACT_APP_SERVER_URL || '';
const FIVE_MINUTES_TIMEOUT = 300000; // 5 minutes

// --- Role-based Defaults ---
// Simple example: users with `ROLE_DEFAULT_PROMPT_PERMISSION` start with system prompt id=93.
// Everyone else starts with no explicit system prompt (null).
// Adjust this permission string to whatever permission/role gate you want.
const ROLE_DEFAULT_PROMPT_PERMISSION = 'chat:productionss';
const ROLE_DEFAULT_PROMPT_ID = 108;

// --- Helper Functions ---
const haveMessagesChanged = (originalMessages = [], currentMessages = []) => {
    if (!originalMessages && !currentMessages) return false;
    if (!originalMessages || !currentMessages) return true;
    if (originalMessages.length !== currentMessages.length) return true;
    try {
        return JSON.stringify(originalMessages) !== JSON.stringify(currentMessages);
    } catch (e) {
        console.error("Error comparing message arrays:", e);
        return true; // Assume changed if comparison fails
    }
};

// --- Main Component ---
// Helper to detect permission denied errors (401/403 or explicit message)
function isPermissionDenied(error) {
    if (!error) return false;
    const status = error.response?.status;
    if (status === 401 || status === 403) return true;
    const msg = (error.response?.data?.error || error.response?.data?.message || error.message || '').toLowerCase();
    if (!msg) return false;
    if (msg.includes('permission') && msg.includes('denied')) return true;
    if (msg.includes('not authorized') || msg.includes('forbidden')) return true;
    return false;
}

const ChatWindow = forwardRef(({ isAuthenticated, handleLogout, authUsername, userLLMSettings, onSettingsSavedInChatWindow, onBackendBusyChange }, ref) => {
    // --- Core State ---
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    // --- ID Glossary ---
    // 1) currentChatId (DB):
    //    - Integer chat-history row id.
    //    - Used ONLY for saving/loading chat history via /api/chat-history/* endpoints.
    //    - Not used by the agent loop.
    // 2) agentChatId (Redis session key):
    //    - UUID string used as the Redis key for the canonical, stateful agent transcript.
    //    - Sent as `chat_id` to /api/agent-messages and /api/pipelines/execute.
    //    - This is NOT the DB chat history id.
    // 3) agentRunId (agent execution id):
    //    - UUID string identifying a specific in-progress agent execution (for cancel/checkpoints).
    //    - Many runs can happen within one agentChatId session.
    const [currentChatId, setCurrentChatId] = useState(null); // DB chat history id (int)
    const [agentChatId, setAgentChatId] = useState(null); // Redis agent session chat_id (UUID string)
    const [selectedChat, setSelectedChat] = useState(null); 
    const [originalLoadedMessages, setOriginalLoadedMessages] = useState([]); 
    const [refreshSidebarKey, setRefreshSidebarKey] = useState(0);
    const [chatHistory, setChatHistory] = useState([]);
    const [activeChatId, setActiveChatId] = useState(null);
    // Runtime agent cheatsheet (kept in sync via SSE)
    const [agentCheatsheet, setAgentCheatsheet] = useState({});
    // Mobile UI: sidebar drawer toggle
    const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

    // --- Agent run/checkpoint state ---
    const [agentRunId, setAgentRunId] = useState(null); // agent execution run_id (UUID string)
    const [cancellingRun, setCancellingRun] = useState(false);
    const agentStepCounterRef = useRef(0);
    const committedAgentStepRef = useRef(0); // Tracks last step saved to backend
    const predictedStepThisTurnRef = useRef(null); // Tracks steps predicted in current turn

    // Monotonic counter used to invalidate in-flight async work (SSE streams, etc.)
    // when the user starts a new chat or we hard-reset local state.
    const chatEpochRef = useRef(0);

    // AbortController for the currently in-flight request/stream.
    // Used to stop SSE streams from continuing after Save & New Chat.
    const activeStreamAbortRef = useRef(null);

    // --- Refs ---
    const inputRef = useRef(null);
    const inputBarRef = useRef(null);
    const [inputBarHeight, setInputBarHeight] = useState(80);

    // --- Chat Settings State ---
    const [isChatSettingsOpen, setIsChatSettingsOpen] = useState(false);
    const [useRAG, setUseRAG] = useState(false);
    const [selectedKnowledgeName, setSelectedKnowledgeName] = useState('');
    const [useText2SQL, setUseText2SQL] = useState(false);
    const [selectedDb, setSelectedDb] = useState('');
    // Defaults: Agent settings ON, no tools selected, loops=20, see thoughts/actions OFF
    const [useAgentAPI, setUseAgentAPI] = useState(true);
    const [seeThoughtsAndActions, setSeeThoughtsAndActions] = useState(false); // keep off by default
    const [loopsBeforeHumanInput, setLoopsBeforeHumanInput] = useState(20);
    const [selectedPipeline, setSelectedPipeline] = useState('');
    const [selectedTools, setSelectedTools] = useState({
        relevantText: false,
        googleSearch: false,
        queryExternalDatabase: false,
        exploreKnowledgeGraph: false,
        searchCollectionKnowledge: false,
        saveTextToFile: false,
        listFilesInDirectory: false,
        readFileContent: false,
        createDirectory: false,
        analyticsEngine: false,
        daxCoder: false,
        queryKnowledgeBase: false,
        listAllMyFiles: false,
        image_annotation: false,
        generate_image: false,
        outlookAssistant: false,
    });
    const [systemPrompt, setSystemPrompt] = useState(''); // Will hold an object {text, name, source, ...}
    const [selectedPromptId, setSelectedPromptId] = useState(null); // NEW: Track selected prompt ID
    const [attachedFiles, setAttachedFiles] = useState([]);

    // Permissions (same source as ProtectedElement)
    const { hasPermission, loading: permissionsLoading } = usePermissions();
    const roleDefaultPromptId = hasPermission(ROLE_DEFAULT_PROMPT_PERMISSION) ? ROLE_DEFAULT_PROMPT_ID : null;

    // --- Popup State ---
    const [collectionNames, setCollectionNames] = useState([]);
    const [showCollectionPopup, setShowCollectionPopup] = useState(false);
    const [collectionFilter, setCollectionFilter] = useState('');
    const [collectionTriggerIndex, setCollectionTriggerIndex] = useState(-1);
    const [loadingCollections, setLoadingCollections] = useState(false);
    const [collectionError, setCollectionError] = useState('');
    const [uniqueRemoteDbs, setUniqueRemoteDbs] = useState([]);
    const [showDbPopup, setShowDbPopup] = useState(false);
    const [dbFilter, setDbFilter] = useState('');
    const [dbTriggerIndex, setDbTriggerIndex] = useState(-1);
    const [loadingDbs, setLoadingDbs] = useState(false);
    const [dbError, setDbError] = useState('');
    const [popupPosition, setPopupPosition] = useState({ bottom: 0, left: 0, width: 0 });

    // --- Explorer Log Modal State ---
    const [isExplorerModalOpen, setIsExplorerModalOpen] = useState(false);
    const [selectedExplorerUUID, setSelectedExplorerUUID] = useState(null);
    const [explorerLogData, setExplorerLogData] = useState(null);
    const [isExplorerModalLoading, setIsExplorerModalLoading] = useState(false);
    const [explorerModalError, setExplorerModalError] = useState('');

     // --- Add State for DocumentViewerModal ---
     const [isViewerOpen, setIsViewerOpen] = useState(false);
     const [viewingFilePath, setViewingFilePath] = useState(null); // Path relative to user's Home

     // --- ADDED: Agent Pipeline Pause State ---
     const [initialPipelineInput, setInitialPipelineInput] = useState(null);
     const [pausedAgentStepConversation, setPausedAgentStepConversation] = useState(null);
     const [pausedAgentStepIndex, setPausedAgentStepIndex] = useState(-1);
     const [pausedPipelineName, setPausedPipelineName] = useState('');
     // --- ADDED: Agent Pipeline Pause State ---

     // --- State and Refs for Virtualization ---
    const [displayedMessageCount, setDisplayedMessageCount] = useState(15); // Show latest 50 messages initially
    const chatAreaRef = useRef(null); // Ref for the scrollable chat area div
    const scrollStateBeforeLoadMore = useRef(null); // Ref to hold scroll position before loading more  
    
    // --- UI: Toggle Cheatsheet Side Panel ---
    const [showCheatsheetPanel, setShowCheatsheetPanel] = useState(false);

    // --- Explorer Log Modal ---
    const handleUUIDClick = useCallback(async (uuid) => {
        if (!uuid) return;
        console.log(`UUID clicked: ${uuid}`);
        setSelectedExplorerUUID(uuid); setExplorerModalError(''); setExplorerLogData(null);
        setIsExplorerModalLoading(true); setIsExplorerModalOpen(true);
        try {
            const response = await axios.get(`${BASE_URL}/api/load_log/${uuid}`, { withCredentials: true });
            if (response.data) {
                 console.log("Fetched explorer log data successfully.");
                 setExplorerLogData(response.data);
            } else { throw new Error("No data received from log endpoint."); }
        } catch (error) {
            let msg = `Failed load log ${uuid.substring(0,8)}...`;
            if (error.response?.status === 401) msg += " Unauthorized.";
            else if (error.response?.status === 404) msg = `Log not found: ${uuid.substring(0,8)}...`;
            else if (error.response?.data?.error) msg += ` ${error.response.data.error}`;
            else if (error.message) msg += ` ${error.message}`;
            console.error(`Error fetching log data for UUID ${uuid}:`, error);
            setExplorerModalError(msg);
        } finally { setIsExplorerModalLoading(false); }
    }, [BASE_URL]);

    const generateAgentChatId = () => {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID();
        }
        // fallback: not perfect UUID, but stable enough for a client-generated id
        return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
        };

    // --- Utility: Hide main system prompt in UI (keep status system messages) ---
    const isStatusSystemMessage = useCallback((msg) => {
        if (!msg || msg.role !== 'system') return false;
        const c = String(msg.content || '');
        // Common status/system notifications we DO want to show
        const patterns = [
            'Agent is PAUSED',
            'Starting Pipeline',
            'Pipeline complete',
            'Cancel', // matches Cancellation requested / Agent stop initiated
            'Saved changes for',
            'Loaded checkpoint',
            'Finalization requested successfully',
        ];
        // if (c.startsWith('---')) return true; // step headers like --- Step ...
        return patterns.some(p => c.includes(p));
    }, []);

    const filterOutMainSystemPrompt = useCallback((arr) => {
        console.log("filterOutMainSystemPrompt called.");
        if (!Array.isArray(arr) || arr.length === 0) return Array.isArray(arr) ? arr : [];
        console.log("Input messages array length:", arr.length);
        const out = arr.slice();

        // Requirement: Remove ALL system messages that appear BEFORE the first
        // user or assistant message. Leave later system messages untouched.
        const firstUAIdx = out.findIndex(m => m && (m.role === 'user' || m.role === 'assistant'));
        if (firstUAIdx === -1) {
            console.log("No user/assistant message found; leaving messages unchanged.");
            return out;
        }

        const prefix = out.slice(0, firstUAIdx);
        const keptPrefix = prefix.filter(m => !(m && m.role === 'system'));
        const removedCount = prefix.length - keptPrefix.length;
        if (removedCount > 0) {
            console.log(`Removed ${removedCount} system message(s) before the first user/assistant message (index ${firstUAIdx}).`);
        } else {
            console.log("No system messages to remove before the first user/assistant message.");
        }
        const suffix = out.slice(firstUAIdx);
        return [...keptPrefix, ...suffix];
    }, []);

    // Notify parent (App.js) when chat/agent backend work is running.
    useEffect(() => {
        const busy = Boolean(loading || cancellingRun);
        if (typeof onBackendBusyChange === 'function') {
            onBackendBusyChange(busy);
        }
        return () => {
            if (typeof onBackendBusyChange === 'function') {
                onBackendBusyChange(false);
            }
        };
    }, [loading, cancellingRun, agentRunId, onBackendBusyChange]);


    // --- Derived Settings ---
    const getCurrentProviderSettings = useCallback(() => {
        return userLLMSettings
            ? { llmProvider: userLLMSettings.llmprovider || 'OpenAI', model: userLLMSettings.model || 'gpt-4o-mini' }
            : { llmProvider: 'OpenAI', model: 'gpt-4o-mini' };
    }, [userLLMSettings]);
    const [settings, setSettings] = useState(getCurrentProviderSettings());
    useEffect(() => { setSettings(getCurrentProviderSettings()); }, [userLLMSettings, getCurrentProviderSettings]);

        // --- START: Process messages for display and virtualization ---
    const processedMessagesForDisplay = useMemo(() => {
        if (seeThoughtsAndActions) {
            return messages; // Show all messages
        }

        // Filter messages: keep system, user, and only the last in a sequence of assistant messages
        const filtered = [];
        for (let i = 0; i < messages.length; i++) {
            const currentMessage = messages[i];
            if (currentMessage.role === 'system' || currentMessage.role === 'user') {
                filtered.push(currentMessage);
            } else if (currentMessage.role === 'assistant') {
                // Check if it's the last message OR the next message is NOT an assistant message
                if (i === messages.length - 1 || messages[i + 1].role !== 'assistant') {
                    filtered.push(currentMessage);
                }
                // Else (it's an assistant message followed by another assistant message), skip it
            } else {
                 // Handle any other unexpected roles by just including them
                 filtered.push(currentMessage);
            }
        }
        return filtered;
    }, [messages, seeThoughtsAndActions]);

    // Replace bespoke virtualization with shared hook (preserve seeThoughtsAndActions behavior)
    const { containerRef: chatAreaRefShared, processed: processedMessagesShared, displayed: displayedMessagesShared, hasMore: hasMoreMessages, loadMore: loadMoreMessages, displayedCount } = useChatDisplay(processedMessagesForDisplay || messages, seeThoughtsAndActions, 15);

    // build markdown options with auth + viewer wiring so links/images are handled
    const sharedMarkdownOptions = useMemo(() => buildMarkdownOptions({
        authUsername,
        BASE_URL,
        setIsViewerOpen,
        setViewingFilePath,
        onFileLink: (path) => {
            const raw = (path || '').trim();
            
            let normalized;
            
            // Case 1: starts with "./" → replace with "Home/"
            if (raw.startsWith('./')) {
                normalized = 'Home/' + raw.slice(2);
            }
            // Case 2: already starts with "Home/" (capital H) → keep as-is
            else if (raw.startsWith('Home/')) {
                normalized = raw;
            }
            // Case 3: starts with "home/" (lowercase) → normalize to "Home/"
            else if (raw.startsWith('home/')) {
                normalized = 'Home/' + raw.slice(5);
            }
            // Case 4: everything else → prepend "Home/"
            else {
                // Remove leading "/" if present, then prepend "Home/"
                normalized = 'Home/' + raw.replace(/^\//, '');
            }

            console.log(`File link clicked: ${path}, opening File Explorer.`);
            console.log(`Normalized path for viewer: ${normalized}`);

            if (normalized) {
                setViewingFilePath(normalized);
                setIsViewerOpen(true);
                return;
            }
            // Fallback: just open File Explorer tab
            setIsChatSettingsOpen(true);
        },
        processNode: makeUUIDProcessor(handleUUIDClick),
    }), [authUsername, BASE_URL, setIsViewerOpen, setViewingFilePath, handleUUIDClick]);

    // --- Effects ---

    // Keep chat-area content clear of the fixed input bar
    useLayoutEffect(() => {
        if (!inputBarRef.current) return;
        const el = inputBarRef.current;
        const update = () => setInputBarHeight(Math.max(56, el.offsetHeight || 56));
        update();
        let ro;
        if (typeof ResizeObserver !== 'undefined') {
            ro = new ResizeObserver(() => update());
            ro.observe(el);
        } else {
            const id = setInterval(update, 500);
            return () => clearInterval(id);
        }
        return () => { try { ro && ro.disconnect(); } catch { /* noop */ } };
    }, []);

    useEffect(() => {
        if (userLLMSettings && userLLMSettings.chat_settings) {
            const cs = userLLMSettings.chat_settings;
            console.log("ChatWindow: Initializing states from userLLMSettings.chat_settings:", cs);
            setUseRAG(cs.useRAG ?? false);
            setSelectedKnowledgeName(cs.selectedKnowledgeName ?? '');
            setUseText2SQL(cs.useText2SQL ?? false);
            setSelectedDb(cs.selectedDb ?? '');
            // Defaults aligned: Agent ON, loops = 10 when not provided
            setUseAgentAPI(cs.useAgentAPI ?? true);
            setLoopsBeforeHumanInput(cs.loopsBeforeHumanInput ?? 10);

            // Tools default: all OFF unless explicitly provided in saved settings
            const defaultToolsState = {
                relevantText: false,
                googleSearch: false,
                queryExternalDatabase: false,
                exploreKnowledgeGraph: false,
                searchCollectionKnowledge: false,
                saveTextToFile: false,
                listFilesInDirectory: false,
                readFileContent: false,
                createDirectory: false,
                analyticsEngine: false,
                daxCoder: false,
                queryKnowledgeBase: false,
                listAllMyFiles: false,
                image_annotation: false,
                generate_image: false,
                outlookAssistant: false,
            };
            setSelectedTools(cs.selectedTools ? { ...defaultToolsState, ...cs.selectedTools } : defaultToolsState);
            setSelectedPipeline(cs.selectedPipeline ?? '');
            setSeeThoughtsAndActions(cs.seeThoughtsAndActions ?? false);

            // System prompt defaulting:
            // - If the backend stored a selectedPromptId, use it.
            // - If stored value is null/undefined, treat that as "role default".
            const storedPromptId = cs.selectedPromptId;
            const effectivePromptId = (storedPromptId === null || storedPromptId === undefined || storedPromptId === '')
                ? roleDefaultPromptId
                : storedPromptId;
            setSelectedPromptId(effectivePromptId ?? null);
            // DO NOT update attachedFiles here, it's session-specific
        } else {
            console.log("ChatWindow: No chat_settings found in userLLMSettings, resetting to defaults.");
            setUseRAG(false);
            setSelectedKnowledgeName('');
            setUseText2SQL(false);
            setSelectedDb('');
            // Defaults aligned: Agent ON, loops = 10
            setUseAgentAPI(true);
            setLoopsBeforeHumanInput(10);
            // All tools OFF by default
            setSelectedTools({
                relevantText: false,
                googleSearch: false,
                queryExternalDatabase: false,
                exploreKnowledgeGraph: false,
                searchCollectionKnowledge: false,
                saveTextToFile: false,
                listFilesInDirectory: false,
                readFileContent: false,
                createDirectory: false,
                analyticsEngine: false,
                daxCoder: false,
                queryKnowledgeBase: false,
                listAllMyFiles: false,
                image_annotation: false,
                generate_image: false,
                outlookAssistant: false,
            });
            setSelectedPipeline('');
            setSeeThoughtsAndActions(false);

            // Default system prompt for this role
            setSelectedPromptId(roleDefaultPromptId ?? null);
            // DO NOT reset attachedFiles here unless explicitly intended for all defaults
        }
    }, [userLLMSettings, roleDefaultPromptId]); 

    // If permissions arrive after initial render, apply role default once (without overriding explicit user choice).
    useEffect(() => {
        if (!isAuthenticated) return;
        if (permissionsLoading) return;
        if (selectedPromptId !== null) return; // already set (either role default or user-selected)
        if (roleDefaultPromptId === null) return;
        setSelectedPromptId(roleDefaultPromptId);
    }, [isAuthenticated, permissionsLoading, roleDefaultPromptId, selectedPromptId]);
    
    const handleSaveSettingsAndPersist = useCallback(async (
        newLoops,
        newTools,
        newPipeline,
        newlyAttachedFiles,
        newUseAgentAPI,
        newSeeThoughts
    ) => {
        console.log("ChatWindow: handleSaveSettingsAndPersist called with:", { newLoops, newTools, newPipeline, newlyAttachedFilesCount: newlyAttachedFiles?.length, newUseAgentAPI, newSeeThoughts });

        // Update ChatWindow's local state for these settings
        setLoopsBeforeHumanInput(newLoops);
        setSelectedTools(newTools);
        setSelectedPipeline(newPipeline);
        setAttachedFiles(newlyAttachedFiles);
        setUseAgentAPI(newUseAgentAPI);
        setSeeThoughtsAndActions(newSeeThoughts);

        setIsChatSettingsOpen(false); // Close the modal

        const chatSettingsPayload = {
            useAgentAPI: newUseAgentAPI,
            loopsBeforeHumanInput: newLoops,
            selectedTools: newTools,
            selectedPipeline: newPipeline,
            seeThoughtsAndActions: newSeeThoughts,
            // Persist current system prompt selection. `null` means "role default".
            selectedPromptId: selectedPromptId ?? null,
        };
    
        const globalLLMSettings = userLLMSettings || {
            llmprovider: 'OpenAI', 
            model: 'gpt-4o-mini',
            temperature: 0.5,
            max_tokens: 2500,
        };
        
        const finalPayload = {
            llmProvider: globalLLMSettings.llmprovider, 
            model: globalLLMSettings.model,
            temperature: (globalLLMSettings.temperature ?? 0.5).toString(), // Ensure ?? for null temp
            maxTokens: (globalLLMSettings.max_tokens ?? 2500).toString(), // Ensure ?? for null tokens
            chatSettings: chatSettingsPayload, 
        };
    
        console.log("ChatWindow: Saving user settings with payload:", finalPayload);
        setLoading(true); 
        setError('');
        try {
            await axios.post(`${BASE_URL}/api/user/settings`, finalPayload, { withCredentials: true });
            console.log("ChatWindow: User settings (including chat_settings) saved successfully.");
            // MODIFIED for Problem 3: Removed alert
            // alert("Chat settings applied and saved!"); 
            
            // MODIFIED for Problem 1: Trigger refresh in App.js
            if (onSettingsSavedInChatWindow) {
                onSettingsSavedInChatWindow();
            }
    
        } catch (err) {
            console.error("ChatWindow: Error saving user settings:", err);
            const errorMsg = err.response?.data?.error || err.message || "Failed to save settings.";
            setError(`Save settings failed: ${errorMsg}`);
        } finally {
            setLoading(false);
        }
    }, [userLLMSettings, BASE_URL, onSettingsSavedInChatWindow, selectedPromptId]); // Added onSettingsSavedInChatWindow

    
    const handleCancelSettingsModal = () => {
        console.log("ChatWindow: Chat settings modal closed without saving (Cancel/X/ESC).");
        setIsChatSettingsOpen(false);
        // No state changes related to settings are made here.
        // Modal's internal temporary changes are discarded.
    };

    const fetchHistory = useCallback(async () => {
        if (!isAuthenticated) return;
        try {
            const response = await axios.get(`${BASE_URL}/api/chat-history`, { withCredentials: true });
            setChatHistory(response.data || []);
        } catch (err) {
            console.error("Failed to fetch chat history:", err);
            if (!isPermissionDenied(err)) {
                setError("Could not load chat history.");
            } else {
                console.info("Permission denied fetching chat history (suppressed).");
            }
        }
    }, [isAuthenticated, BASE_URL]);

    // useEffect(() => {
    //     fetchHistory();
    // }, [fetchHistory]); 

    useEffect(() => {
        fetchHistory();
    }, [fetchHistory, refreshSidebarKey]);

    const fetchCollectionNames = useCallback(async () => {
        setLoadingCollections(true); setCollectionError('');
        try {
            const response = await axios.get(`${BASE_URL}/api/files/get_collection_names`, { withCredentials: true });
            if (Array.isArray(response.data)) { setCollectionNames(response.data); }
            else { setCollectionNames([]); setCollectionError("Invalid data format received."); }
        } catch (error) {
            setCollectionNames([]);
            if (!isPermissionDenied(error)) {
                setCollectionError(error.response?.data?.error || error.message || "Could not load collection names.");
            } else {
                console.info("Permission denied fetching collection names (suppressed).");
            }
        } finally { setLoadingCollections(false); }
    }, [BASE_URL]);

    const fetchDbNames = useCallback(async () => {
        setLoadingDbs(true); setDbError('');
        try {
            const response = await axios.get(`${BASE_URL}/api/unique_remote_dbs`, { withCredentials: true });
            if (Array.isArray(response.data)) { setUniqueRemoteDbs(response.data); }
            else { setUniqueRemoteDbs([]); setDbError("Invalid data format received."); }
        } catch (error) {
            setUniqueRemoteDbs([]);
            if (!isPermissionDenied(error)) {
                setDbError(error.response?.data?.error || error.message || "Could not load database names.");
            } else {
                console.info("Permission denied fetching DB names (suppressed).");
            }
        } finally { setLoadingDbs(false); }
    }, [BASE_URL]);

    useEffect(() => {
        fetchCollectionNames();
        fetchDbNames();
    }, [fetchCollectionNames, fetchDbNames]);

    const clearLocalStateAndResetOriginals = useCallback((isNewChatStart = true) => {
        console.log("Clearing local chat state. New chat start:", isNewChatStart);

        // Invalidate any in-flight stream callbacks from the previous chat/session.
        chatEpochRef.current += 1;

        // Also abort any in-flight request so we don't get late stream completion.
        try {
            if (activeStreamAbortRef.current) {
                activeStreamAbortRef.current.abort();
            }
        } catch (_) { /* ignore */ }
        activeStreamAbortRef.current = null;

        setMessages([]);
        setInput('');
        setError('');
        setAttachedFiles([]);
        setLoading(false);
        setDisplayedMessageCount(15);
        setAgentCheatsheet({});

        setAgentRunId(null);
        setAgentChatId(null);
        agentStepCounterRef.current = 0;
    
        setPausedAgentStepConversation(null);
        setPausedAgentStepIndex(-1);
        setPausedPipelineName('');
        setInitialPipelineInput('');
    
        if (isNewChatStart) {
            setCurrentChatId(null);
            setSelectedChat(null);
            setOriginalLoadedMessages([]);
        }
    
        setSelectedDb('');
        setShowCollectionPopup(false); setCollectionFilter(''); setCollectionTriggerIndex(-1);
        setShowDbPopup(false); setDbFilter(''); setDbTriggerIndex(-1);
        setIsExplorerModalOpen(false); setSelectedExplorerUUID(null); setExplorerLogData(null);
        setExplorerModalError(''); setIsExplorerModalLoading(false);
    }, []);

    const saveChatIfNeeded = useCallback(async () => {
        // This is the condition for an unsaved chat.
        if (!selectedChat && messages.length > 0) {
            setLoading(true);
            try {
                console.log("Auto-saving unsaved chat before navigation...");
                // Use the same API endpoint as "Save & New Chat"
                const messagesWithSnapshot = appendCheatsheetSnapshotMessage(messages);
                await axios.post(`${BASE_URL}/api/save-chat-history`, { messages: messagesWithSnapshot, promptName: null }, { withCredentials: true });
                console.log("Unsaved chat saved successfully.");
                setRefreshSidebarKey(prev => prev + 1); // Refresh sidebar to show the newly saved chat
                clearLocalStateAndResetOriginals(true); // Clean up the chat window state
                return true; // Indicate success
            } catch (err) {
                console.error("Failed to auto-save chat:", err);
                setLoading(false); // Stop loading indicator on failure
                // Throw an error to be caught by the caller in App.js
                if (isPermissionDenied(err)) {
                    console.info("Permission denied auto-saving chat (suppressed). Leaving chat unsaved.");
                    return true; // treat as non-fatal
                }
                throw new Error("Failed to save chat.");
            }
        }
        // If not "dirty", resolve to true immediately.
        return true;
    }, [selectedChat, messages, clearLocalStateAndResetOriginals, BASE_URL]);


    // Expose the function to the parent component (App.js) via the ref
    useImperativeHandle(ref, () => ({
        handleBeforeNavigate: async () => {
            // Check if there are unsaved changes.
            if (!selectedChat && messages.length > 0) {
                try {
                    await saveChatIfNeeded();
                    return true; // Saved successfully, navigation can proceed.
                } catch (err) {
                    console.error("ChatWindow: Error saving user settings:", err);
                    const errorMsg = err.response?.data?.error || err.message || "Failed to save settings.";
                    if (!isPermissionDenied(err)) {
                        setError(`Save settings failed: ${errorMsg}`);
                    } else {
                        console.info("Permission denied saving user settings (suppressed).\n");
                    }
                    // In either case (suppressed or real error), fall through to ask confirmation
                    if (!isPermissionDenied(err)) {
                        return window.confirm("Failed to save your current chat. Are you sure you want to leave and discard your changes?");
                    } else {
                        // If permission denied, allow navigation silently
                        return true;
                    }
                }
            }
            // No unsaved changes, so navigation can proceed.
            return true;
        },

        // Used for auto-logout: attempt to persist an unsaved chat WITHOUT showing confirm dialogs.
        // Returns true if either (a) there was nothing to save, or (b) save succeeded, or (c) save was blocked by permissions.
        // Returns false if saving failed for other reasons.
        saveUnsavedChatSilently: async () => {
            try {
                await saveChatIfNeeded();
                return true;
            } catch (err) {
                console.error('ChatWindow: Silent save failed:', err);
                if (!isPermissionDenied(err)) {
                    const msg = err?.message || 'Failed to save chat.';
                    setError(msg);
                    return false;
                }
                // If permission denied, allow logout/navigation silently.
                return true;
            }
        },
    }));

    useEffect(() => {
        if (!isAuthenticated) {
            clearLocalStateAndResetOriginals(true);
        }
    }, [isAuthenticated, clearLocalStateAndResetOriginals]);

    // --- Chat History Management ---
    const CHEATSHEET_SNAPSHOT_TAG = 'CHEATSHEET_SNAPSHOT::';
    const appendCheatsheetSnapshotMessage = useCallback((msgs) => {
        try {
            const snapshot = JSON.stringify(agentCheatsheet || {});
            const tag = `${CHEATSHEET_SNAPSHOT_TAG}${snapshot}`;
            return [...msgs, { role: 'system', content: tag }];
        } catch (_) { return msgs; }
    }, [agentCheatsheet]);

    const extractCheatsheetFromHistory = useCallback((msgs) => {
        if (!Array.isArray(msgs)) return { cleaned: msgs || [], cheatsheet: {} };
        let latest = {};
        const cleaned = [];
        for (const m of msgs) {
            if (m?.role === 'system' && typeof m.content === 'string' && m.content.startsWith(CHEATSHEET_SNAPSHOT_TAG)) {
                const jsonStr = m.content.substring(CHEATSHEET_SNAPSHOT_TAG.length);
                try { latest = JSON.parse(jsonStr) || latest; } catch (_) { /* ignore parse */ }
                continue; // omit snapshot message from visible history
            }
            cleaned.push(m);
        }
        return { cleaned, cheatsheet: latest };
    }, []);

    const handleNewChat = useCallback(() => {
        setError('');
        const performSaveAndReset = (isNewChatStart = true) => {
            setLoading(true);
            console.log(`Attempting to save chat (currentChatId: ${currentChatId || 'New'})...`);
            const promptNameToSave = selectedChat?.prompt_name || null;
            const messagesWithSnapshot = appendCheatsheetSnapshotMessage(messages);
            axios.post(`${BASE_URL}/api/save-chat-history`, { messages: messagesWithSnapshot, promptName: promptNameToSave }, { withCredentials: true })
                .then(response => {
                    console.log("Chat saved successfully, backend assigned new entry.");
                    clearLocalStateAndResetOriginals(isNewChatStart);
                    setRefreshSidebarKey(prev => prev + 1);
                })
                .catch(err => {
                    console.error("Failed to save chat:", err);
                    const errorMsg = err.response?.data?.error || err.message || "Could not save the chat.";
                    if (err.response?.status === 401) {
                        setError("Authentication failed. Please log in again.");
                        if (handleLogout) handleLogout();
                    } else { setError(`Failed to save chat: ${errorMsg}`); }
                    setLoading(false);
                });
        };

        if (!selectedChat && messages.length > 0) {
            console.log("handleNewChat: Saving current new/forked chat.");
            performSaveAndReset(true);
        } else {
            console.log("handleNewChat: No changes to save, or empty new buffer. Clearing for new chat.");
            clearLocalStateAndResetOriginals(true);
        }
    }, [messages, selectedChat, currentChatId, clearLocalStateAndResetOriginals, BASE_URL, handleLogout]);

    const handleSelectChat = useCallback((chatData) => {
        if (chatData === null) {
            clearLocalStateAndResetOriginals(true);
            return;
        }

        if (!chatData || !chatData.id) {
            console.warn("handleSelectChat called with invalid chatData:", chatData);
            return;
        }
        console.log("Selected chat from sidebar:", chatData.id);

        const isCurrentBufferUnsaved = !selectedChat && messages.length > 0;
        if (isCurrentBufferUnsaved) {
            if (!window.confirm("You have an unsaved chat in progress. Load selected chat and discard current changes?")) {
                return;
            }
        }

        clearLocalStateAndResetOriginals(false);
        setSelectedChat(chatData);
        setCurrentChatId(chatData.id);

        // Parse and load chat history
        const loadChatHistory = async () => {
            if (!chatData || !chatData.id) return;

            const epochAtLoadStart = chatEpochRef.current;

            try {
                const response = await axios.get(`${BASE_URL}/api/chat-history/${chatData.id}`, { withCredentials: true });
                if (response.status === 200) {
                    const data = response.data;
                    // Process and set the loaded chat history
                    let loadedMessages = [];
                    if (data.chat_history) {
                        try {
                            let history = data.chat_history;
                            if (typeof history === 'string') { try { history = JSON.parse(history); } catch { /* ignore */ } }
                            if (typeof history === 'string') { try { history = JSON.parse(history); } catch (e) { console.error("Double JSON parse failed", e); history = null; } }

                                if (Array.isArray(history) && history.every(msg => msg && typeof msg.role === 'string' && typeof msg.content !== 'undefined')) {
                                // Preserve object content when present (do not force String for objects)
                                const historyArray = history.map(msg => ({ ...msg, content: (typeof msg.content === 'object' && msg.content !== null) ? msg.content : String(msg.content || '') }));
                                const { cleaned, cheatsheet } = extractCheatsheetFromHistory(historyArray);
                                loadedMessages = cleaned;
                                if (cheatsheet && Object.keys(cheatsheet).length > 0) {
                                    setAgentCheatsheet(cheatsheet);
                                }
                                // Hide the main system prompt from display
                                loadedMessages = filterOutMainSystemPrompt(loadedMessages);
                            } else {
                                setError("Could not load chat history: Invalid format.");
                            }
                        } catch (e) { setError("Failed to parse chat history."); }
                    }

                    if (chatEpochRef.current !== epochAtLoadStart) {
                        return;
                    }
                    setMessages(loadedMessages);
                    const annotated = annotateAgentStepsFromMessages(loadedMessages);
                    setMessages(annotated);
                    setOriginalLoadedMessages([...annotated]);
                    const baseCommitted = getLastCommittedAgentStep(annotated);
                    committedAgentStepRef.current = baseCommitted;
                    agentStepCounterRef.current = baseCommitted;
                    predictedStepThisTurnRef.current = null;
                    setOriginalLoadedMessages([...loadedMessages]);
                    setLoading(false);
                }
            } catch (error) {
                console.error("Failed to load chat history:", error);
                if (chatEpochRef.current === epochAtLoadStart) {
                    setError("Failed to load chat history.");
                }
            }
        };

        loadChatHistory();
    }, [messages, selectedChat, clearLocalStateAndResetOriginals]);


    // --- Settings Modal Management ---
    // (legacy handler removed; ChatSettingsModal now saves via handleSaveSettingsAndPersist)

    // --- Agent/Pipeline Helpers ---
    const attemptRequest = useCallback(async (currentMessages, provider, tools, chatIdToUse, currentSystemPromptText = '', currentSystemPromptID = null, retry = 0, agentJobId = null) => {
        console.log(`Calling Agent API (Retry: ${retry}) for Chat ID: ${chatIdToUse || 'New Chat (will be assigned by save)'}`);
        setLoading(true); setError('');
        
        const messagesForAgent = [];
        let systemPromptFoundInMessages = false;
        currentMessages.forEach(msg => {
             messagesForAgent.push(msg);
             if (msg.role === 'system') systemPromptFoundInMessages = true;
        });

        // Add system prompt text to messages array if it's not already there and is provided
        if (currentSystemPromptText && !systemPromptFoundInMessages && !messagesForAgent.some(m => m.role === 'system')) {
            messagesForAgent.unshift({ role: 'system', content: currentSystemPromptText });
        }
        
        try {
            const payload = {
                messages: messagesForAgent,
                human_loop: loopsBeforeHumanInput,
                provider: provider || 'OpenAI',
                selectedTools: tools,
                chat_id: chatIdToUse,
                system_prompt_text: currentSystemPromptText || null, // Actual text for the system message
                system_prompt_id: currentSystemPromptID || null,  // ID of the selected prompt
                agent_job_id: agentJobId,
            };
            console.log("Payload to /api/agent-messages:", JSON.stringify(payload, null, 2)); // Log the full payload
            const response = await axios.post(
                `${BASE_URL}/api/agent-messages`,
                payload,
                { timeout: FIVE_MINUTES_TIMEOUT, withCredentials: true }
            );
            return response.data;
        } catch (error) {
             if ((error.code === 'ECONNABORTED' || error.response?.status === 504 || error.response?.status === 502) && retry < 1) {
                 await new Promise(resolve => setTimeout(resolve, 1500));
                 return await attemptRequest(currentMessages, provider, tools, chatIdToUse, currentSystemPromptText, currentSystemPromptID, retry + 1);
             } else {
                 let specificErrorMsg = error.message || 'Failed to receive response after retry.';
                 if (error.response) {
                    specificErrorMsg = error.response.data?.error || specificErrorMsg;
                    if (error.response.status === 401) { setError("Authentication failed."); if (handleLogout) handleLogout(); }
                    else { setError(`Agent Error: ${specificErrorMsg}`); }
                 } else { setError(`Agent Error: ${specificErrorMsg}`); }
                 throw error;
             }
        }
    }, [loopsBeforeHumanInput, BASE_URL, handleLogout]);

    const fetchPipelinePrompts = useCallback(async (pipelineName) => {
        if (!pipelineName) return [];
        try {
            const response = await axios.get(`${BASE_URL}/api/prompt-pipelines/details-by-name/${pipelineName}`, { withCredentials: true });
            if (!Array.isArray(response.data) || !response.data.every(item => typeof item === 'object' && item !== null && !Array.isArray(item))) {
                throw new Error('Invalid data format: expected array of objects.');
            }
            return response.data;
        } catch (error) {
            let errorMsg = `Failed to load prompts for pipeline ${pipelineName}.`;
             if (error.response?.status === 401) { errorMsg = "Auth failed fetching pipeline."; if (handleLogout) handleLogout(); }
             else if (error.response?.status === 404) { errorMsg = `Pipeline '${pipelineName}' not found.`; }
             else { errorMsg += ` ${error.message}`; }
            setError(prev => `${prev ? prev + ' | ' : ''}${errorMsg}`);
            return [];
        }
    }, [BASE_URL, handleLogout]);

    const fetchAttachedFileContent = useCallback(async (filePaths) => {
        if (!filePaths || filePaths.length === 0) return '';
        try {
            const response = await axios.post(`${BASE_URL}/api/file_explorer/read_user_files`, { filePaths }, { withCredentials: true });
            if (response.data?.errors?.length > 0) {
                 setError(prev => `${prev ? prev + ' | ' : ''}Warning: Could not read some files. ${response.data.errors.join(', ')}`);
            }
            return response.data?.combinedContent || '';
        } catch (error) {
            let errorMsg = "Failed to load content for attached files.";
            if (error.response?.status === 401) errorMsg += " Unauthorized.";
            else if (error.response?.data?.error) errorMsg = error.response.data.error;
            else if (error.message) errorMsg += ` ${error.message}.`
            setError(prev => `${prev ? prev + ' | ' : ''}${errorMsg}`);
            return '';
        }
    }, [BASE_URL]);

    // Helper: annotate messages with _agentStep so UI can show per-step actions
    const annotateAgentStepsFromMessages = useCallback((arr) => {
        // Detect messages that are ONLY an object like { thought, name, parameters }
        const isAgentOnlyActionMessage = (content) => {
            if (content === null || content === undefined) return false;
            // If content is already an object, check keys directly
            if (typeof content === 'object' && !Array.isArray(content)) {
                const parsed = content;
                const keys = Object.keys(parsed).sort();
                if (keys.length !== 3) return false;
                if (keys[0] === 'name' && keys[1] === 'parameters' && keys[2] === 'thought') {
                    return typeof parsed.parameters === 'object' && parsed.parameters !== null && !Array.isArray(parsed.parameters);
                }
                return false;
            }
            let s = String(content).trim();
            // strip code fences if present
            if (s.startsWith('```') && s.endsWith('```')) {
                s = s.replace(/^```+/, '').replace(/```+$/, '').trim();
            }
            try {
                const parsed = JSON.parse(s);
                if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return false;
                const keys = Object.keys(parsed).sort();
                // exact keys: thought, name, parameters
                if (keys.length !== 3) return false;
                if (keys[0] === 'name' && keys[1] === 'parameters' && keys[2] === 'thought') {
                    return typeof parsed.parameters === 'object' && parsed.parameters !== null && !Array.isArray(parsed.parameters);
                }
                return false;
            } catch (e) {
                return false;
            }
        };

        let step = 0;
        const result = (arr || []).map(m => ({ ...m }));
        for (const m of result) {
            if (m.role === 'assistant') {
                const text = String(m.content || '');
                // If this assistant message is purely an agent-action object, skip it
                if (isAgentOnlyActionMessage(text)) {
                    // do not increment step; do not tag with _agentStep
                    continue;
                }
                if (!text.includes('Action_Response for')) {
                    step += 1; // new step begins at assistant's non-action_response message
                }
                m._agentStep = step || undefined; // tag assistant messages in the step
            }
        }
        return result;
    }, []);

    const getLastCommittedAgentStep = useCallback((arr) => {
        // Same detection as annotateAgentStepsFromMessages: ignore pure agent-action objects
        const isAgentOnlyActionMessage = (content) => {
            if (content === null || content === undefined) return false;
            let s = String(content).trim();
            if (s.startsWith('```') && s.endsWith('```')) {
                s = s.replace(/^```+/, '').replace(/```+$/, '').trim();
            }
            try {
                const parsed = JSON.parse(s);
                if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return false;
                const keys = Object.keys(parsed).sort();
                if (keys.length !== 3) return false;
                if (keys[0] === 'name' && keys[1] === 'parameters' && keys[2] === 'thought') {
                    return typeof parsed.parameters === 'object' && parsed.parameters !== null && !Array.isArray(parsed.parameters);
                }
                return false;
            } catch (e) {
                return false;
            }
        };

        let lastStep = 0;
        for (const m of (arr || [])) {
            if (m.role === 'assistant') {
                const text = String(m.content || '');
                if (isAgentOnlyActionMessage(text)) {
                    continue; // ignore these for counting
                }
                if (!text.includes('Action_Response for')) {
                    lastStep += 1; // new step begins at assistant's non-action_response message
                }
            }
        }
        return lastStep;
    }, []);

    const loadAgentContextAtStep = useCallback(async (stepNumber) => {
        const epochAtLoadStart = chatEpochRef.current;
        if (!useAgentAPI) {
            setError("Agent features are disabled.");
            return;
        }
        if (!agentRunId) {
            setError("No active agent run to load context from.");
            return;
        }
        // if (!Number.isInteger(stepNumber) || stepNumber < 1) {
        //     setError("Invalid step number.");
        //     return;
        // }

        setLoading(true); setError('');
        try {
            const response = await axios.post(`${BASE_URL}/api/load-agent-checkpoint`,
                { run_id: agentRunId, step_index: stepNumber, chat_id: agentChatId },
                { withCredentials: true }
            );

            if (chatEpochRef.current !== epochAtLoadStart) {
                return;
            }
            
            const state = response.data?.state;
            if (!state) {
                setError(`No state data returned for step ${stepNumber}.`);
                setLoading(false);
                return;
            }

            let restoredMessages = Array.isArray(state.messages) ? state.messages.map(m => ({ ...m })) : [];
            // Hide the main system prompt on checkpoint load
            console.log("Restoring messages from checkpoint at step", stepNumber, "Total messages:", restoredMessages.length);
            restoredMessages = filterOutMainSystemPrompt(restoredMessages);
            const annotatedMessages = annotateAgentStepsFromMessages(restoredMessages);
            
            setMessages(annotatedMessages);
            setOriginalLoadedMessages(annotatedMessages);
            if (state.cheatsheet && typeof state.cheatsheet === 'object') {
                setAgentCheatsheet(state.cheatsheet);
            }
            
            // Commit to the selected global step
            committedAgentStepRef.current = stepNumber;
            agentStepCounterRef.current = stepNumber;
            predictedStepThisTurnRef.current = null;
            setAgentRunId(agentRunId);

            // Set paused UI so next action is user-driven
            setPausedAgentStepConversation(restoredMessages);
            setPausedAgentStepIndex(stepNumber);
            
            if (state.collection) setSelectedKnowledgeName(state.collection);

        } catch (error) {
            console.error("Failed to load checkpoint:", error);
            const msg = error?.response?.data?.detail || error.message || "Failed to load checkpoint.";
            setError(msg);
        } finally {
            if (chatEpochRef.current === epochAtLoadStart) {
                setLoading(false);
            }
        }
    }, [BASE_URL, agentRunId, annotateAgentStepsFromMessages, useAgentAPI]);

    // --- Main Send Handler ---
    const handleSend = useCallback(async (manualForceNextStep = false, manualContinue = false) => {
        // Snapshot the current epoch so we can ignore late SSE events
        // after the user starts a new chat.
        const epochAtSendStart = chatEpochRef.current;

        // Abort any previous in-flight stream and create a controller for this turn.
        try {
            if (activeStreamAbortRef.current) {
                activeStreamAbortRef.current.abort();
            }
        } catch (_) { /* ignore */ }
        const abortController = new AbortController();
        activeStreamAbortRef.current = abortController;

        const isCurrentEpoch = () => chatEpochRef.current === epochAtSendStart;

        // Guard against React passing the click/keyboard event as the first arg when used as onClick handler
        const isEvent = (v) => v && typeof v === 'object' && (('nativeEvent' in v) || ('preventDefault' in v) || ('target' in v && 'currentTarget' in v));
        const forceNext = isEvent(manualForceNextStep) ? false : !!manualForceNextStep;
        const continueStep = isEvent(manualContinue) ? false : !!manualContinue;

        if ((!forceNext && !continueStep) && (!input.trim() || loading || showCollectionPopup || showDbPopup)) return;

        let userMessageContentBase = input.trim();
        let userMessageContentToSend = userMessageContentBase;
        let currentMessages = [...messages];
        // IMPORTANT: we maintain two distinct ids depending on which backend API we are calling:
        // - DB chat history endpoints use `currentChatId` (int)
        // - Agent/pipeline endpoints use `agentChatId` (UUID string Redis session key)
        let effectiveDbChatIdForHistory = currentChatId;
        let effectiveAgentSessionChatId = agentChatId;

        // Track whether we're starting a brand-new agent chat session this turn.
        // (We generate chat_id client-side so the backend can use it as the Redis key.)
        let createdNewAgentChatId = false;

        let forkedFromHistory = false;

        console.log(`Initial user input: ${userMessageContentBase}`);

        if (selectedChat && selectedChat.id) {
            console.log(`Forking from selected historical chat ${selectedChat.id}. This will become a new chat session upon saving.`);
            setOriginalLoadedMessages([...currentMessages]);
            setSelectedChat(null);
            setCurrentChatId(null);
            setAgentChatId(null);
            effectiveDbChatIdForHistory = null;
            effectiveAgentSessionChatId = null;
            forkedFromHistory = true;
            setPausedAgentStepConversation(null);
            setPausedAgentStepIndex(-1);
            setPausedPipelineName('');
        }

        // Ensure we have a valid agent chat_id AFTER any fork/reset logic.
        // We seed the Redis-backed transcript on the first request if we're starting a new session.
        if (useAgentAPI && !effectiveAgentSessionChatId) {
            const newId = generateAgentChatId();
            setAgentChatId(newId);
            effectiveAgentSessionChatId = newId;
            createdNewAgentChatId = true;
        }

        let systemPromptAddition = '';
        let filesUsedInSend = [...attachedFiles];
        let operationSucceeded = false;

        setError(''); setLoading(true); setInput('');

        if (!useAgentAPI || !selectedPipeline || (selectedPipeline && selectedPipeline !== pausedPipelineName)) {
            if (pausedAgentStepConversation) {
                console.log("New operation or different pipeline selected, clearing previously paused agent step.");
                setPausedAgentStepConversation(null);
                setPausedAgentStepIndex(-1);
                setPausedPipelineName('');
            }
        }

        if (filesUsedInSend.length > 0) {
            if (useAgentAPI) {
                const formattedPaths = filesUsedInSend.map(p => `- ./${p}`).join('\n');
                userMessageContentToSend = `Attached Files:\n${formattedPaths}\n\n${userMessageContentBase}`;
            } else {
                const combinedContent = await fetchAttachedFileContent(filesUsedInSend);
                if (!isCurrentEpoch()) {
                    return;
                }
                if (combinedContent) {
                    systemPromptAddition = `\n\n--- Attached File Content ---\n${combinedContent}\n--- End Attached File`;
                } else {
                    filesUsedInSend = [];
                }
            }
        }

    if (continueStep) {
            userMessageContentToSend = 'Continue';
        }

        if (useAgentAPI) {
            currentMessages.push({ role: 'user', content: userMessageContentToSend });
            setMessages(prev => [...prev, { role: 'user', content: userMessageContentToSend }]);
        } else if (!useAgentAPI || !selectedPipeline) {
            setMessages(prev => [...prev, { role: 'user', content: userMessageContentToSend }]);
        }
        
        console.log("handleSend: Current systemPrompt state:", JSON.stringify(systemPrompt, null, 2));
        const selectedPromptText = typeof systemPrompt === 'object' ? (systemPrompt?.text || '') : '';
        // Prefer the dedicated state `selectedPromptId` (it supports role defaults even when prompt text isn't loaded).
        const selectedPromptID = (selectedPromptId !== null && selectedPromptId !== undefined)
            ? selectedPromptId
            : ((typeof systemPrompt === 'object' && systemPrompt.id !== undefined) ? systemPrompt.id : null);
        console.log("handleSend: Extracted selectedPromptID:", selectedPromptID);

        const effectiveSystemPromptText = selectedPromptText + systemPromptAddition;
        const currentProvider = userLLMSettings?.llmprovider || settings.llmProvider;

        let wasPausedThisTurn = false;

        try {
            let responseData = {};
            if (useAgentAPI && selectedPipeline) {
                // --- PIPELINE AGENT LOGIC (SSE streaming to handle pause/resume) ---
                console.log(`Starting Agent Pipeline: ${selectedPipeline}`);

                // Prepare payload for backend
                let payload = {
                    pipeline_name: selectedPipeline,
                    initial_user_input: initialPipelineInput ? initialPipelineInput : userMessageContentToSend,
                    // Pipelines run in the same Redis-backed agent session.
                    chat_id: effectiveAgentSessionChatId,
                    selected_tools: selectedTools,
                    human_loop_setting: loopsBeforeHumanInput,
                    force_next_step: forceNext,
                    // Pass current agent cheatsheet so pipelines can maintain state across steps
                    cheatsheet: agentCheatsheet || {}
                };

                // If resuming a paused pipeline, add resume params
                if (pausedAgentStepConversation && pausedPipelineName === selectedPipeline && pausedAgentStepIndex >= 0) {
                    payload.paused_step_index = pausedAgentStepIndex;
                    payload.accumulated_messages = [
                        ...pausedAgentStepConversation
                    ];
                    payload.new_user_input = userMessageContentToSend;
                    // For resume, do not send initial_user_input (backend will ignore it)
                } else {
                    setInitialPipelineInput(userMessageContentToSend);
                }
                // Now that we've used the paused state, clear it
                setPausedAgentStepConversation(null);
                setPausedAgentStepIndex(-1);
                setPausedPipelineName('');

                let pipelineMessages = [...currentMessages];
                let pausedStepIndex = -1;
                let pausedStepConversation = null;
                let pausedPipelineNameLocal = '';
                let pipelineCompleted = false;

                setLoading(true);

                try {
                    const response = await fetch(`${BASE_URL}/api/pipelines/execute`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Accept': 'text/event-stream',
                            'Cache-Control': 'no-cache'
                        },
                        body: JSON.stringify(payload),
                        credentials: 'include',
                        signal: abortController.signal,
                    });
                    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
                    if (!response.body) throw new Error('No response body from pipeline execution.');

                    // Dynamically import to avoid circular deps if any
                    const { streamSSE } = await import('./chat/streamSSE.js');
                    const { default: makeSSEHandlers } = await import('./chat/makeSSEHandlers.js');

                    const handlers = makeSSEHandlers(async (eventType, parsed) => {
                        if (chatEpochRef.current !== epochAtSendStart) {
                            // User started a new chat/reset while this stream was running.
                            // Stop processing to avoid leaking old messages into the new chat.
                            return true;
                        }
                        if (eventType === 'pipeline_started') {
                            const serverChatId = parsed.chat_id;
                            if (serverChatId) {
                                effectiveAgentSessionChatId = serverChatId;
                                setAgentChatId(serverChatId);
                                console.log('Pipeline started, assigned Chat ID:', serverChatId);
                            }
                        } else if (eventType === 'cheatsheet') {
                            // Pipeline executor emits the cheatsheet object directly (not wrapped)
                            try { setAgentCheatsheet(parsed || {}); } catch (_) {}
                        } else if (eventType === 'step_start') {
                            pipelineMessages.push({
                                role: 'system',
                                content: `--- Starting Pipeline: ${selectedPipeline} - Step ${parsed.step}/${parsed.total_steps} ---\nUsing Prompt: "${parsed.name}"`
                            });
                            setMessages([...pipelineMessages]);
                        } else if (eventType === 'new_message') {
                            pipelineMessages.push(parsed);
                            setMessages([...pipelineMessages]);
                        } else if (eventType === 'cancelled') {
                            loadAgentContextAtStep(null);
                            setError('Agent run cancelled by user.');
                            setLoading(false);
                            return true; // stop stream
                        } else if (eventType === 'pipeline_paused') {
                            pausedStepIndex = parsed.paused_step_index;
                            pausedStepConversation = parsed.accumulated_messages;
                            pausedPipelineNameLocal = selectedPipeline;
                            pipelineMessages.push({
                                role: 'system',
                                content: `Agent is PAUSED (performed ${loopsBeforeHumanInput} steps...)`
                            });
                            setPausedAgentStepConversation(pausedStepConversation);
                            setPausedAgentStepIndex(pausedStepIndex);
                            setPausedPipelineName(pausedPipelineNameLocal);
                            setMessages([...pipelineMessages]);
                            setLoading(false);
                            wasPausedThisTurn = true;
                            return true; // stop stream
                        } else if (eventType === 'pipeline_complete') {
                            pipelineCompleted = true;
                            setPausedAgentStepConversation(null);
                            setPausedAgentStepIndex(-1);
                            setPausedPipelineName('');
                            pipelineMessages.push({ role: 'system', content: parsed.message });
                            setMessages([...pipelineMessages]);
                            setLoading(false);
                            setOriginalLoadedMessages([...pipelineMessages]);
                            operationSucceeded = true;
                            return true; // stop
                        } else if (eventType === 'error') {
                            setError('Pipeline error: ' + (parsed.message || 'Unknown error.'));
                            setLoading(false);
                            return true; // stop
                        }
                        return false; // continue
                    });

                    await streamSSE(response, handlers);
                    // After stream ends (naturally or by stop flag)
                    if (isCurrentEpoch()) {
                        setOriginalLoadedMessages([...pipelineMessages]);
                        operationSucceeded = pipelineCompleted;
                    }
                } catch (err) {
                    if (err?.name === 'AbortError') {
                        return;
                    }
                    if (!isCurrentEpoch()) {
                        return;
                    }
                    console.error('Error in pipeline execution:', err);
                    setError(err.message || 'Pipeline execution error');
                    setLoading(false);
                }
                return;

            } else if (useAgentAPI) { 
                if (pausedAgentStepConversation) {
                    setPausedAgentStepConversation(null);
                    setPausedAgentStepIndex(-1);
                    setPausedPipelineName('');
                }
                console.log("Using Single Agent API (SSE). The backend will loop up to", loopsBeforeHumanInput, "times.");
                
                let agentMessages = [...currentMessages];
                setLoading(true);

                const annotatedHistory = annotateAgentStepsFromMessages(agentMessages);
                setMessages(annotatedHistory);
                const baseCommitted = getLastCommittedAgentStep(annotatedHistory);
                committedAgentStepRef.current = baseCommitted;
                agentStepCounterRef.current = baseCommitted; // keep existing callers working
                    predictedStepThisTurnRef.current = null;
                    try {
                        const clientMessageId = (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
                            ? crypto.randomUUID()
                            : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

                        const useActionApi = !!effectiveAgentSessionChatId;
                        const actionType = createdNewAgentChatId ? 'start' : 'user_message';

                        // If we just created a new Redis-backed chat_id in the middle of an existing
                        // on-screen conversation (e.g., after loading chat history), seed the server-side
                        // transcript so the model retains full context on subsequent turns.
                        let seedMessages = null;
                        if (createdNewAgentChatId && Array.isArray(agentMessages) && agentMessages.length > 0) {
                            seedMessages = agentMessages.slice();
                            const last = seedMessages[seedMessages.length - 1];
                            if (last && last.role === 'user' && String(last.content) === String(userMessageContentToSend)) {
                                seedMessages.pop();
                            }
                        }

                        const payload = useActionApi
                            ? {
                                ...(seedMessages ? { messages: seedMessages } : {}),
                                human_loop: loopsBeforeHumanInput,
                                selectedTools: selectedTools,
                                chat_id: effectiveAgentSessionChatId,
                                system_prompt_id: selectedPromptID || null,
                                run_id: agentRunId || null,
                                cheatsheet: agentCheatsheet || {},
                                action: {
                                    type: actionType,
                                    content: userMessageContentToSend
                                },
                                client_message_id: clientMessageId
                            }
                            : {
                                // Legacy fallback: pass full transcript when we don't have a Redis chat session
                                messages: agentMessages,
                                human_loop: loopsBeforeHumanInput,
                                selectedTools: selectedTools,
                                chat_id: effectiveAgentSessionChatId,
                                system_prompt_id: selectedPromptID || null,
                                run_id: agentRunId || null,
                                cheatsheet: agentCheatsheet || {}
                            };
                        const response = await fetch(`${BASE_URL}/api/agent-messages`, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Accept': 'text/event-stream',
                                'Cache-Control': 'no-cache'
                            },
                            body: JSON.stringify(payload),
                            credentials: 'include',
                            signal: abortController.signal,
                        });
                        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
                        if (!response.body) throw new Error('No response body from agent execution.');

                        const { streamSSE } = await import('./chat/streamSSE.js');
                        const { default: makeSingleAgentSSEHandlers } = await import('./chat/makeSingleAgentSSEHandlers.js');

                        let agentOutputMessages = [...agentMessages];
                        // Helper: detect agent-only action messages (object with exact keys thought,name,parameters)
                        const isAgentOnlyActionMessage = (content) => {
                            if (content === null || content === undefined) return false;
                            if (typeof content === 'object' && !Array.isArray(content)) {
                                const parsed = content;
                                const keys = Object.keys(parsed).sort();
                                if (keys.length !== 3) return false;
                                if (keys[0] === 'name' && keys[1] === 'parameters' && keys[2] === 'thought') {
                                    return typeof parsed.parameters === 'object' && parsed.parameters !== null && !Array.isArray(parsed.parameters);
                                }
                                return false;
                            }
                            let s = String(content).trim();
                            if (s.startsWith('```') && s.endsWith('```')) {
                                s = s.replace(/^```+/, '').replace(/```+$/, '').trim();
                            }
                            try {
                                const parsed = JSON.parse(s);
                                if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return false;
                                const keys = Object.keys(parsed).sort();
                                if (keys.length !== 3) return false;
                                if (keys[0] === 'name' && keys[1] === 'parameters' && keys[2] === 'thought') {
                                    return typeof parsed.parameters === 'object' && parsed.parameters !== null && !Array.isArray(parsed.parameters);
                                }
                                return false;
                            } catch (e) {
                                return false;
                            }
                        };

                        const isPrimaryAssistantMessage = (msgObj) => {
                            if (!msgObj || msgObj.role !== 'assistant') return false;
                            // If this is an agent-only action message, it's NOT a primary assistant step
                            if (isAgentOnlyActionMessage(msgObj.content)) return false;
                            const text = String(msgObj.content || '');
                            return !text.includes('Action_Response for');
                        };
                        const commitPredictedStep = () => {
                            if (predictedStepThisTurnRef.current != null) {
                                committedAgentStepRef.current = predictedStepThisTurnRef.current;
                                agentStepCounterRef.current = committedAgentStepRef.current;
                                predictedStepThisTurnRef.current = null;
                            }
                        };
                        const startNewPredictedStep = () => {
                            const next = (committedAgentStepRef.current || 0) + 1;
                            predictedStepThisTurnRef.current = next;
                            return next;
                        };

                        const handlers = makeSingleAgentSSEHandlers((eventType, parsed) => {
                            if (chatEpochRef.current !== epochAtSendStart) {
                                // User started a new chat/reset while this stream was running.
                                // Stop processing to avoid leaking old messages into the new chat.
                                return true;
                            }
                            if (eventType === 'run_started') {
                                if (parsed.run_id) {
                                    setAgentRunId(parsed.run_id);
                                    if (typeof parsed.latest_step === 'number') {
                                        committedAgentStepRef.current = parsed.latest_step;
                                        agentStepCounterRef.current = parsed.latest_step;
                                        predictedStepThisTurnRef.current = null;
                                    }
                                    console.log('Agent run started, assigned Run ID:', parsed.run_id);
                                }
                            } else if (eventType === 'cheatsheet') {
                                console.log("Received updated agent cheatsheet.");
                                try {
                                    setAgentCheatsheet(parsed || {});
                                } catch (_) { /* ignore */ }
                                console.log('Updated agent cheatsheet:', parsed);
                            } else if (eventType === 'message') {
                                const msg = parsed;
                                if (msg && msg.role === 'assistant') {
                                    // Skip agent-only action messages for step counting/annotation
                                    if (isAgentOnlyActionMessage(msg.content)) {
                                        // do not touch predicted/committed counters; do not set _agentStep
                                    } else if (isPrimaryAssistantMessage(msg)) {
                                        if (predictedStepThisTurnRef.current != null) {
                                            commitPredictedStep();
                                        }
                                        const newPredicted = startNewPredictedStep();
                                        msg._agentStep = newPredicted;
                                    } else {
                                        if (predictedStepThisTurnRef.current != null) {
                                            msg._agentStep = predictedStepThisTurnRef.current;
                                        } else {
                                            msg._agentStep = committedAgentStepRef.current;
                                        }
                                    }
                                }
                                agentOutputMessages.push(msg);
                                setMessages([...agentOutputMessages]);
                            } else if (eventType === 'cancelled') {
                                console.log("Agent run was cancelled by user.");
                                loadAgentContextAtStep(null);
                                setError('Agent run cancelled by user.');
                                setLoading(false);
                                return true; // stop stream
                            } else if (eventType === 'paused') {
                                commitPredictedStep();
                                agentOutputMessages.push({
                                    role: 'system',
                                    content: parsed.message || 'Agent is PAUSED.'
                                });
                                setMessages([...agentOutputMessages]);
                                setLoading(false);
                                return true; // stop
                            } else if (eventType === 'complete') {
                                commitPredictedStep();
                                setMessages([...agentOutputMessages]);
                                setLoading(false);
                                return true; // stop
                            } else if (eventType === 'error') {
                                setError(parsed.error || parsed.message || 'Agent error.');
                                setLoading(false);
                                return true; // stop
                            }
                            return false; // continue
                        });

                        await streamSSE(response, handlers);
                    } catch (err) {
                        if (err?.name === 'AbortError') {
                            return;
                        }
                        if (!isCurrentEpoch()) {
                            return;
                        }
                        setError(err.message || 'An unexpected error occurred.');
                        setMessages(originalLoadedMessages);
                        setLoading(false);
                    }

            } else { // Non-Agent Logic
                if (pausedAgentStepConversation) {
                    setPausedAgentStepConversation(null);
                    setPausedAgentStepIndex(-1);
                    setPausedPipelineName('');
                }
                const commonConfig = { timeout: FIVE_MINUTES_TIMEOUT, withCredentials: true, signal: abortController.signal };
                const baseMessagesForCall = [ ...(effectiveSystemPromptText ? [{ role: 'system', content: effectiveSystemPromptText }] : []), ...messages, { role: 'user', content: userMessageContentToSend } ];
                let finalAssistantMessageContent = '';

                if (useText2SQL) {
                    if (!selectedDb) throw new Error('Text2SQL enabled, but no database selected.');
                    const t2sPayload = { userQuery: userMessageContentToSend, dbSelection: selectedDb, chatId: effectiveDbChatIdForHistory };
                    const response = await axios.post(`${BASE_URL}/api/text2sql`, t2sPayload, commonConfig);
                    if (!isCurrentEpoch()) {
                        return;
                    }
                    responseData = response.data;
                    const executedQuery = responseData.query || 'N/A'; const resultsJson = responseData.results || '[]'; let resultsDisplay = '(No results)'; try { const d = JSON.parse(resultsJson); if(Array.isArray(d) && d.length>0){ const h = Object.keys(d[0]); resultsDisplay = `| ${h.join(' | ')} |\n| ${h.map(()=>'---').join(' | ')} |\n${d.map(r=>`| ${h.map(k=>String(r[k]??'').replace(/\|/g,'\\|')).join(' | ')} |`).join('\n')}`; } else if(Array.isArray(d)){ resultsDisplay = '(0 rows returned)';}} catch(e){ console.error("Error parsing T2S results JSON", e);} const qf = responseData.queryFile ? `\n\n*Results saved: ${responseData.queryFile}*` : '';
                    finalAssistantMessageContent = `**Text2SQL Result (DB: \`${selectedDb}\`)**\n\n**Executed Query:**\n\`\`\`sql\n${executedQuery}\n\`\`\`\n**Results:**\n${resultsDisplay}${qf}`;
                } else if (useRAG) {
                    const ragPayload = { userInput: userMessageContentToSend, messages: messages, systemPrompt: effectiveSystemPromptText, selectedCollectionName: selectedKnowledgeName, chat_id: effectiveDbChatIdForHistory };
                    const response = await axios.post(`${BASE_URL}/api/rag-chat`, ragPayload, commonConfig);
                    if (!isCurrentEpoch()) {
                        return;
                    }
                    responseData = response.data;
                    finalAssistantMessageContent = responseData.content;
                } else {
                    // This is the standard prochat call. It will use the userLLMSettings for provider, model, temp, tokens.
                    // The system prompt is already prepended to baseMessagesForCall if effectiveSystemPromptText exists.
                    const proChatPayload = { messages: baseMessagesForCall, chat_id: effectiveDbChatIdForHistory };
                    const response = await axios.post(`${BASE_URL}/api/prochat`, proChatPayload, commonConfig);
                    if (!isCurrentEpoch()) {
                        return;
                    }
                    responseData = response.data;
                    finalAssistantMessageContent = responseData;
                }
                if (isCurrentEpoch()) {
                    setMessages(prev => [...prev, { role: 'assistant', content: finalAssistantMessageContent }]);
                }
            }
            operationSucceeded = true;

            if (isCurrentEpoch()) {
                setMessages(currentMessagesAfterResponse => {
                    setOriginalLoadedMessages([...currentMessagesAfterResponse]);
                    return currentMessagesAfterResponse;
                });
            }

        } catch (err) {
            if (err?.name === 'AbortError' || err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') {
                return;
            }
            if (!isCurrentEpoch()) {
                return;
            }
            setError(err.message || "An unexpected error occurred.");
            setMessages(originalLoadedMessages);

            // if (forkedFromHistory) {
            //     // This logic to revert a forked chat remains the same.
            //     setSelectedChat({ id: currentChatId, /* Need original chat data here */ });
            //     setCurrentChatId(currentChatId);
            //     setMessages([...originalLoadedMessages]);
            // }
        } finally {
            // Clear this turn's abort controller if still active.
            if (activeStreamAbortRef.current === abortController) {
                activeStreamAbortRef.current = null;
            }

            // Never write state into a new chat session.
            if (isCurrentEpoch()) {
                if (operationSucceeded && filesUsedInSend.length > 0 && attachedFiles.length > 0) {
                    setAttachedFiles([]);
                }

                if (wasPausedThisTurn) {
                    // setLoading(false) was already called inside the loop before `return;`.
                    console.log("handleSend finished: Pipeline is paused and waiting for user input. UI is active.");
                } else {
                    console.log("handleSend finished: Request complete for this turn. Unlocking UI.");
                    setLoading(false);
                }
            }
        }
    }, [
        input, loading, showCollectionPopup, showDbPopup, messages, attachedFiles, selectedChat, currentChatId,
        useAgentAPI, fetchAttachedFileContent, systemPrompt, userLLMSettings, settings.llmProvider,
        selectedPipeline, selectedTools, loopsBeforeHumanInput, 
        fetchPipelinePrompts, attemptRequest, handleLogout, useText2SQL, selectedDb,
        useRAG, selectedKnowledgeName, error, originalLoadedMessages,
        pausedAgentStepConversation, pausedAgentStepIndex, pausedPipelineName,
    ]);

    const handleContinuePipelineStep = useCallback(() => {
        // Only allow if pipeline is paused
        if (
            pausedAgentStepConversation &&
            pausedAgentStepIndex >= 0 &&
            pausedPipelineName === selectedPipeline
        ) {
            // Resume pipeline from the paused step
            setTimeout(() => {
                handleSend(true, false); // Force send with manual completion
            }, 200); // Let state update before calling handleSend
        }
        else {
            console.warn("Cannot continue pipeline step: No paused step found or pipeline name mismatch.");
        }   
    }, [
        pausedAgentStepConversation,
        pausedAgentStepIndex,
        pausedPipelineName,
        selectedPipeline,
        handleSend
    ]);

    // --- Cancel Active Agent Run ---
    const handleCancelAgentRun = useCallback(async () => {
        if (!agentRunId || !loading || cancellingRun) return;
        setCancellingRun(true);
        try {
            // Optimistic UI update: add system message
            setMessages(prev => ([...prev, { role: 'system', content: `Cancellation requested for agent run ${agentRunId}...` }]));
            const resp = await fetch(`${BASE_URL}/api/agent-stop`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ run_id: agentRunId })
            });
            if (!resp.ok) {
                const txt = await resp.text().catch(() => '');
                setError(prev => `${prev ? prev + ' | ' : ''}Cancel failed (${resp.status}): ${txt || 'Unknown error'}`);
            } else {
                const data = await resp.json().catch(() => ({}));
                setMessages(prev => ([...prev, { role: 'system', content: data.message || 'Agent stop initiated.' }]));
            }
        } catch (e) {
            setError(prev => `${prev ? prev + ' | ' : ''}Cancel error: ${e.message || e}`);
        } finally {
            // We do not immediately clear loading; backend stream should terminate or send final event.
            setCancellingRun(false);
        }
    }, [agentRunId, loading, cancellingRun, BASE_URL]);

    // --- Popup Handlers ---
    const handleInputChange = (event) => {
        const currentInput = event.target.value;
        const cursorPos = event.target.selectionStart;
        setInput(currentInput);

        const collectionTrigger = "||";
        const dbTrigger = "|\\";
        let triggerDetected = false;

        const dbTriggerStart = currentInput.lastIndexOf(dbTrigger, cursorPos - dbTrigger.length);
        if (dbTriggerStart !== -1 && dbTriggerStart === cursorPos - dbTrigger.length) {
            triggerDetected = true; setShowCollectionPopup(false);
            const filter = currentInput.substring(dbTriggerStart + dbTrigger.length, cursorPos);
            setDbFilter(filter); setDbTriggerIndex(dbTriggerStart);
            if (!showDbPopup) {
                if (inputBarRef.current) { const rect = inputBarRef.current.getBoundingClientRect(); setPopupPosition({ bottom: window.innerHeight - rect.top - window.scrollY + 5, left: rect.left + window.scrollX, width: rect.width });}
                setShowDbPopup(true);
            }
        } else if (showDbPopup) {
            if (cursorPos >= dbTriggerIndex + dbTrigger.length && currentInput.startsWith(dbTrigger, dbTriggerIndex)) {
                setDbFilter(currentInput.substring(dbTriggerIndex + dbTrigger.length, cursorPos)); triggerDetected = true;
            } else { setShowDbPopup(false); setDbFilter(''); setDbTriggerIndex(-1); }
        }

        if (!triggerDetected || (triggerDetected && !showDbPopup)) {
            const collectionTriggerStart = currentInput.lastIndexOf(collectionTrigger, cursorPos - collectionTrigger.length);
            if (collectionTriggerStart !== -1 && collectionTriggerStart === cursorPos - collectionTrigger.length) {
                triggerDetected = true; setShowDbPopup(false);
                const filter = currentInput.substring(collectionTriggerStart + collectionTrigger.length, cursorPos);
                setCollectionFilter(filter); setCollectionTriggerIndex(collectionTriggerStart);
                if (!showCollectionPopup) {
                    if (inputBarRef.current) { const rect = inputBarRef.current.getBoundingClientRect(); setPopupPosition({ bottom: window.innerHeight - rect.top - window.scrollY + 5, left: rect.left + window.scrollX, width: rect.width });}
                    setShowCollectionPopup(true);
                }
            } else if (showCollectionPopup) {
                if (cursorPos >= collectionTriggerIndex + collectionTrigger.length && currentInput.startsWith(collectionTrigger, collectionTriggerIndex)) {
                    setCollectionFilter(currentInput.substring(collectionTriggerIndex + collectionTrigger.length, cursorPos)); triggerDetected = true;
                } else { setShowCollectionPopup(false); setCollectionFilter(''); setCollectionTriggerIndex(-1); }
            }
        }
        if (!triggerDetected) {
            if (showCollectionPopup) { setShowCollectionPopup(false); }
            if (showDbPopup) { setShowDbPopup(false); }
        }
    };
    const handleCollectionSelect = (selectedName) => {
        if (collectionTriggerIndex === -1) return;
        const currentInput = inputRef.current.value; const triggerSequence = "||";
        const textBefore = currentInput.substring(0, collectionTriggerIndex);
        const filterEndIndex = collectionTriggerIndex + triggerSequence.length + collectionFilter.length;
        const textAfter = currentInput.substring(filterEndIndex);
        const newInput = `${textBefore}${selectedName}${textAfter}`;
        setInput(newInput); setShowCollectionPopup(false); setCollectionFilter(''); setCollectionTriggerIndex(-1);
        setTimeout(() => { if (inputRef.current) { inputRef.current.focus(); const newCursorPos = collectionTriggerIndex + selectedName.length; inputRef.current.setSelectionRange(newCursorPos, newCursorPos);}}, 0);
    };
    const handleDbSelect = (selectedName) => {
        if (dbTriggerIndex === -1) return;
        const currentInput = inputRef.current.value; const triggerSequence = "|\\";
        const textBefore = currentInput.substring(0, dbTriggerIndex);
        const filterEndIndex = dbTriggerIndex + triggerSequence.length + dbFilter.length;
        const textAfter = currentInput.substring(filterEndIndex);
        const newInput = `${textBefore}${selectedName}${textAfter}`;
        setInput(newInput); setShowDbPopup(false); setDbFilter(''); setDbTriggerIndex(-1);
        setTimeout(() => { if (inputRef.current) { inputRef.current.focus(); const newCursorPos = dbTriggerIndex + selectedName.length; inputRef.current.setSelectionRange(newCursorPos, newCursorPos);}}, 0);
    };
    const handleInputBlur = () => {
        setTimeout(() => {
            if (!document.activeElement?.closest('.input-suggestion-popup')) {
                setShowCollectionPopup(false); setShowDbPopup(false);
            }
        }, 150);
    };
    const filteredCollections = Array.isArray(collectionNames) ? collectionNames.filter(name => name.toLowerCase().includes(collectionFilter.toLowerCase())) : [];
    const filteredDbs = Array.isArray(uniqueRemoteDbs) ? uniqueRemoteDbs.filter(name => name.toLowerCase().includes(dbFilter.toLowerCase())) : [];
    
    
    // --- Render ---
    const newChatButtonText = () => {
        if (!selectedChat && messages.length > 0) return '+ Save & New Chat';
        return '+ New Chat';
    };

    return (
        <div className={`chat-window-container ${isMobileSidebarOpen ? 'mobile-sidebar-open' : ''}`}>
            {/* {isDev && (
                <div style={{ textAlign: 'center', margin: '10px 0' }}>
                    <button onClick={testStream} style={{ background: '#007bff', color: '#fff', border: 'none', borderRadius: 4, padding: '8px 18px', fontWeight: 500, cursor: 'pointer' }}>
                        Test SSE Stream (Console)
                    </button>
                </div>
            )} */}
            <div id="chat-sidebar" className="chat-sidebar-wrapper" role="complementary" aria-label="Chat history sidebar">
                <ChatHistorySidebar
                    history={chatHistory}
                    onChatSelect={(chat) => { handleSelectChat(chat); setIsMobileSidebarOpen(false); }}
                    activeChatId={currentChatId}
                    onNewChat={handleNewChat}
                    refreshHistory={() => setRefreshSidebarKey(k => k + 1)} 
                />
                <ProtectedElement required="chat:save-history" behavior="disable" disabledTooltip="Requires chat:save-history permission">
                    <button onClick={() => { handleNewChat(); setIsMobileSidebarOpen(false); }} className="new-chat-button" disabled={loading} title={newChatButtonText()} >
                        {newChatButtonText()}
                    </button>
                </ProtectedElement>
                <DocumentViewerModal
                    isOpen={isViewerOpen}
                    onClose={() => {
                        setIsViewerOpen(false);
                        setViewingFilePath(null); // Clear the path when closing
                    }}
                    filePath={viewingFilePath} // This should be path relative to Home
                    username={authUsername}
                />
            </div>

            <div className="chat-main-area">
                {/* Mobile: Sidebar toggle (hamburger) */}
                <button
                    className="mobile-sidebar-toggle"
                    title="Open chat history"
                    aria-label="Open chat history"
                    aria-expanded={isMobileSidebarOpen}
                    aria-controls="chat-sidebar"
                    onClick={() => setIsMobileSidebarOpen(true)}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M3 6h18v2H3V6zm0 5h18v2H3v-2zm0 5h18v2H3v-2z"/></svg>
                </button>
                {/* <ProtectedElement required="chat:pro" fallbackVariant="overlay"> */}
                <div className="chat-area" ref={chatAreaRef} style={{ paddingBottom: (inputBarHeight || 80) + 16 }}>
                    {/* Floating cheatsheet toggle (less intrusive than input bar) */}
                    <button
                        className="cheatsheet-fab"
                        onClick={() => setShowCheatsheetPanel(true)}
                        title={showCheatsheetPanel ? 'Hide Cheatsheet' : 'Show Cheatsheet'}
                        aria-label="Toggle Cheatsheet"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M7 3h10a2 2 0 0 1 2 2v14l-7-3-7 3V5a2 2 0 0 1 2-2z"/></svg>
                        <span>Cheatsheet</span>
                    </button>
                    {!loading && messages.length === 0 && !error && (
                        <ProtectedElement required="chat:pro" fallbackVariant="overlay">
                        <div className="empty-chat-placeholder">
                            <h2>Pearlify Chat</h2>
                            <p>Select a past chat or start typing below.</p>
                            {userLLMSettings ? (
                                <p className="settings-info">
                                    Using: <strong>{userLLMSettings.llmprovider || 'N/A'}</strong> - {userLLMSettings.model || 'N/A'}
                                    (Temp: {typeof userLLMSettings.temperature === 'number' ? userLLMSettings.temperature.toFixed(1) : 'N/A'}
                                    , Max Tokens: {typeof userLLMSettings.max_tokens === 'number' ? userLLMSettings.max_tokens.toLocaleString() : 'N/A'})
                                </p>
                            ) : ( <p className="settings-info">LLM settings missing or not loaded.</p> )}
                        </div>
                        </ProtectedElement>
                    )}
                    <div className="messages-list">
                        <ProtectedElement required="chat:pro" fallbackVariant="overlay">
                            <ChatMessagesList
                                messages={displayedMessagesShared}
                                loading={loading}
                                error={error}
                                onLoadMore={loadMoreMessages}
                                hasMore={hasMoreMessages}
                                containerRef={chatAreaRefShared}
                                scrollContainerRef={chatAreaRef} /* outer scroll container */
                                markdownOptions={sharedMarkdownOptions}
                                showAgentStepButtons={true}
                                onLoadAgentStep={loadAgentContextAtStep}
                                onUUIDClick={handleUUIDClick}
                                chatId={currentChatId}
                                displayedCount={displayedMessagesShared}
                                allCount={processedMessagesForDisplay}
                                seeThoughtsAndActions={seeThoughtsAndActions}
                                fullTranscript={messages}
                            />
                        </ProtectedElement>
                    </div>
                </div>
                {/* </ProtectedElement> */}
                {useAgentAPI && selectedPipeline && pausedAgentStepConversation && pausedAgentStepIndex >= 0 && pausedPipelineName === selectedPipeline && (
                    <div style={{ textAlign: 'center', margin: '16px 0' }}>
                        <div style={{ marginBottom: 8, fontSize: '1.05em', color: '#444' }}>
                            The agent is paused. You can:
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginBottom: 8 }}>
                            <ProtectedElement required="chat:agent-messages" behavior="disable" disabledTooltip="Requires chat:agent-messages permission">
                            <button
                                onClick={() => {
                                    handleSend(false, true);
                                }}
                                style={{
                                    background: '#007bff',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: 6,
                                    padding: '10px 22px',
                                    fontWeight: 600,
                                    fontSize: '1.08em',
                                    cursor: 'pointer',
                                    boxShadow: '0 1px 4px rgba(0,0,0,0.07)'
                                }}
                            >
                                Continue Step
                            </button>
                            </ProtectedElement>
                            <ProtectedElement required="pipelines:execute" behavior="disable" disabledTooltip="Requires chat:agent-messages permission">
                            <button
                                onClick={handleContinuePipelineStep}
                                style={{
                                    background: '#2b9348',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: 6,
                                    padding: '10px 22px',
                                    fontWeight: 600,
                                    fontSize: '1.08em',
                                    cursor: 'pointer',
                                    boxShadow: '0 1px 4px rgba(0,0,0,0.07)'
                                }}
                            >
                                Terminate Step &amp; Move to Next
                            </button>
                            </ProtectedElement>
                        </div>
                        <div style={{ fontSize: '0.97em', color: '#555', marginTop: 4 }}>
                            Or type any feedback or guidance below and send it to the agent.
                        </div>
                    </div>
                )}
                <div className="chat-input-bar" ref={inputBarRef}>
                    {attachedFiles.length > 0 && (
                        <div className="attachment-count-indicator" title={`${attachedFiles.length} file(s) attached. View/clear in Settings.`} >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16"><path d="M4.5 3a2.5 2.5 0 0 1 5 0v9a1.5 1.5 0 0 1-3 0V5a.5.5 0 0 1 1 0v7a.5.5 0 0 0 1 0V3a1.5 1.5 0 1 0-3 0v9a2.5 2.5 0 0 0 5 0V5a.5.5 0 0 1 1 0v7a3.5 3.5 0 1 1-7 0z"/></svg>
                            <span>{attachedFiles.length}</span>
                        </div>
                     )}
                    <textarea
                        ref={inputRef} className="chat-input" value={input}
                        placeholder="Type message... (|| for collections, |\ for DBs, Shift+Enter for newline)"
                        onChange={handleInputChange} onBlur={handleInputBlur}
                        onKeyDown={event => { if (event.key === 'Enter' && !event.shiftKey && !loading && !showCollectionPopup && !showDbPopup) { event.preventDefault(); handleSend(false, false); }}}
                        rows={Math.max(1, Math.min(input.split('\n').length, 5))} disabled={loading}
                    />
                    <div className="chat-buttons">
                        <ProtectedElement required="chat:pro" behavior="disable" disabledTooltip="Requires chat:pro permission">
                            { /* Cancel button: active only while loading and we have a run id (single-agent mode) */ }
                            <button
                                onClick={handleCancelAgentRun}
                                disabled={!loading || !agentRunId || cancellingRun}
                                className="chat-button cancel"
                                title={agentRunId ? (loading ? 'Cancel running agent' : 'No active run') : 'No agent run id yet'}
                                style={{
                                    background: (!loading || !agentRunId) ? '#bbb' : '#dc2626',
                                    color: '#fff',
                                    opacity: (!loading || !agentRunId) ? 0.5 : 1,
                                    cursor: (!loading || !agentRunId) ? 'not-allowed' : 'pointer'
                                }}
                            >
                                {cancellingRun ? 'Cancelling...' : 'Cancel'}
                            </button>
                            <button onClick={() => handleSend(false, false)} disabled={loading || !input.trim() || showCollectionPopup || showDbPopup} className="chat-button send" title="Send Message" >
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16"><path d="M15.854.146a.5.5 0 0 1 .11.54l-5.819 14.547a.75.75 0 0 1-1.329.124l-3.178-4.995L.643 7.184a.75.75 0 0 1 .124-1.33L15.314.037a.5.5 0 0 1 .54.11ZM6.636 10.07l2.761 4.338L14.13 2.576zm6.787-8.201L1.591 6.602l4.339 2.76z"/></svg>
                            </button>
                        </ProtectedElement>
                        <button onClick={() => setIsChatSettingsOpen(true)} disabled={loading} className="chat-button settings" title="Chat Settings" >
                           <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16"><path d="M9.405 1.05c-.413-1.4-2.397-1.4-2.81 0l-.1.34a1.464 1.464 0 0 1-2.105.872l-.31-.17c-1.283-.698-2.686.705-1.987 1.987l.169.311a1.464 1.464 0 0 1 .872 2.105l-.34.1c-1.4.413-1.4 2.397 0 2.81l.34.1a1.464 1.464 0 0 1-.872 2.105l-.17.31c-.698 1.283.705 2.686 1.987 1.987l.311-.169a1.464 1.464 0 0 1 2.105.872l.1.34c.413 1.4 2.397 1.4 2.81 0l.1-.34a1.464 1.464 0 0 1 2.105-.872l.31.17c1.283.698 2.686-.705 1.987-1.987l-.169-.311a1.464 1.464 0 0 1 .872 2.105l.34-.1c1.4-.413-1.4-2.397 0-2.81l-.34-.1a1.464 1.464 0 0 1-.872-2.105l.17-.31c.698-1.283-.705-2.686-1.987-1.987l-.311.169a1.464 1.464 0 0 1-2.105-.872zM8 10.93a2.929 2.929 0 1 1 0-5.86 2.929 2.929 0 0 1 0 5.858z"/></svg>
                        </button>
                        {/* Cheatsheet toggle moved to a floating button (top-right). See CheatsheetModal.css .cheatsheet-fab */}
                    </div>
                    {showCollectionPopup && ( <div className="input-suggestion-popup collection-popup" style={{ position: 'fixed', bottom: popupPosition.bottom, left: popupPosition.left, minWidth: '200px', maxWidth: `calc(${popupPosition.width}px - 90px)` }} > {loadingCollections && <div className="popup-loading">Loading...</div>} {collectionError && <div className="popup-error">{collectionError}</div>} {!loadingCollections && !collectionError && filteredCollections.length === 0 && ( <div className="popup-no-results">No matching.</div> )} {!loadingCollections && !collectionError && filteredCollections.length > 0 && ( <ul className="popup-list"> {filteredCollections.map(name => ( <li key={name} className="popup-item" onMouseDown={e => { e.preventDefault(); handleCollectionSelect(name); }} > {name} </li> ))} </ul> )} </div> )}
                    {showDbPopup && ( <div className="input-suggestion-popup db-popup" style={{ position: 'fixed', bottom: popupPosition.bottom, left: popupPosition.left, minWidth: '200px', maxWidth: `calc(${popupPosition.width}px - 90px)` }} > {loadingDbs && <div className="popup-loading">Loading...</div>} {dbError && <div className="popup-error">{dbError}</div>} {!loadingDbs && !dbError && filteredDbs.length === 0 && ( <div className="popup-no-results">No matching.</div> )} {!loadingDbs && !dbError && filteredDbs.length > 0 && ( <ul className="popup-list"> {filteredDbs.map(name => ( <li key={name} className="popup-item" onMouseDown={e => { e.preventDefault(); handleDbSelect(name); }} > {name} </li> ))} </ul> )} </div> )}
                </div>
            </div>
            {/* Mobile backdrop when sidebar is open */}
            <div
                className="mobile-backdrop"
                onClick={() => setIsMobileSidebarOpen(false)}
                role="button"
                aria-label="Close sidebar"
                tabIndex={-1}
            />
            <CheatsheetModal open={!!showCheatsheetPanel} onClose={() => setShowCheatsheetPanel(false)} agentCheatsheet={agentCheatsheet} />

            <ChatSettingsModal
                isOpen={isChatSettingsOpen}
                onSave={handleSaveSettingsAndPersist}
                onCancel={handleCancelSettingsModal}
                initialUseAgentAPI={useAgentAPI}
                initialLoopsBeforeHumanInput={loopsBeforeHumanInput}
                initialSelectedTools={selectedTools}
                initialSelectedPipeline={selectedPipeline}
                initialSeeThoughtsAndActions={seeThoughtsAndActions}
                selectedPromptId={selectedPromptId}
                onSelectPrompt={(promptObj) => {
                    // Interpret "default" in the UI as "role default".
                    const requestedId = promptObj?.id ?? null;
                    const effectiveId = (requestedId === null) ? (roleDefaultPromptId ?? null) : requestedId;
                    setSystemPrompt({ ...(promptObj || {}), id: effectiveId });
                    setSelectedPromptId(effectiveId);
                }}
                initialAttachedFiles={attachedFiles}
                authUsername={authUsername}
                forceActiveTab={'File Explorer'}
                initialViewerPath={viewingFilePath}
            />
            <ExplorerLogModal
                isOpen={isExplorerModalOpen}
                onClose={() => {
                    setIsExplorerModalOpen(false);
                    setSelectedExplorerUUID(null);
                    setExplorerLogData(null);
                    setExplorerModalError('');
                }}
                logData={explorerLogData}
                isLoading={isExplorerModalLoading}
                error={explorerModalError}
            />
        </div>
    );
});

export default ChatWindow;